import type { PredictionContract } from '@/services/prediction/types'

import { getPersonInitials, getTitleInitials } from '../utils/abbreviatePersonName'
import type { TeamColor } from '../utils/color'
import { getPlayerImage } from '../utils/getPlayerImage'

/** 一行 futures/recurring outcome 的前导视觉 + 强调色解析结果。 */
export interface OutcomeVisuals {
  /** 选手头像/国旗 —— 透传 CompetitorLogo。 */
  playerImage?: { src?: string; isFlag: boolean }
  /** 队伍主色（hex）—— 驱动胜率下划线。 */
  primaryColor?: string
  /** 队徽 / 国旗 URL。 */
  logoUrl?: string
  /** 无图时 fallback badge 上的 initials（选手 "JL" / 队伍 "LAL"）。 */
  fallbackText?: string
  /** 完全省略前导视觉列（任意 outcome / recurring 行）。 */
  hideLogo?: boolean
}

interface ResolveArgs {
  contract: PredictionContract
  /** recurring 滚动事件（duration > 0）—— 行内不渲染前导视觉。 */
  isRecurring: boolean
  /** sports 资产才走队伍 logo 分支（非 sports 永不当队伍处理）。 */
  isSports: boolean
  /**
   * `useGetTeamColor` 调色板解析结果（已含 dark rescue）。镜像 cdc-web：有
   * 调色板色优先于 `contract.team.color`——无 team 的合约（F1 车手、FX
   * strike）按 id 排序索引从 COLOR_PALETTE 分配，保证每行必有下划线色。
   */
  teamColor?: TeamColor | null
}

/**
 * Port of cdc-web `FutureCard/resolveOutcomeVisuals.ts`，裁剪点：
 * - `isNational` 不产出（cdc-web CompetitorLogo 里该 prop 已是不影响渲染的
 *   兼容保留——所有视觉统一圆角方块，OG-8486）。
 *
 * 优先级（镜像 og-app `resolveRowVisuals`）：
 *   1. recurring 行            → hideLogo，仅保留下划线颜色
 *   2. 候选人/选手             → headshot + 姓名首字母
 *   3. sports 队伍 future      → logo + 颜色 + abbrev fallback
 *   4. 仅颜色的 predict outcome → hideLogo + 彩色下划线
 *   5. 任意 outcome            → hideLogo
 */
export function resolveOutcomeVisuals({
  contract,
  isRecurring,
  isSports,
  teamColor,
}: ResolveArgs): OutcomeVisuals {
  // Tier 1 — recurring 行只出名字：资产图标在页头，行内只保留下划线颜色。
  if (isRecurring) {
    return { hideLogo: true, primaryColor: teamColor?.color ?? contract.team?.color }
  }

  // Tier 2 — 候选人/选手：头像，回落姓名首字母。
  if (contract.individual_profile) {
    return {
      playerImage: getPlayerImage(contract),
      fallbackText: getPersonInitials(contract.contract_title, {
        firstName: contract.individual_profile.first_name,
        lastName: contract.individual_profile.last_name,
      }),
    }
  }

  // Tier 3 — sports 队伍 future：logo + 颜色强调 + abbrev fallback。
  // "有队伍"的判定是 abbrev/color/logo 任一（cdc-web OG-7544 review：
  // 只看 abbrev 会把 BE 没下发 abbrev 但有 color/logo 的队伍错落到 tier-5）。
  const primaryColor = teamColor?.color ?? contract.team?.color
  const logoUrl = teamColor?.logoUrl ?? contract.team?.logo_url ?? undefined
  const hasTeam = isSports && (!!contract.team?.abbrev || !!primaryColor || !!logoUrl)
  if (hasTeam) {
    return {
      primaryColor,
      logoUrl,
      fallbackText:
        contract.team?.abbrev?.toUpperCase() || getTitleInitials(contract.contract_title),
    }
  }

  // Tier 4 — 带颜色的 predict outcome：颜色驱动胜率下划线，无前导视觉
  // （Figma 2466-105875 politics 彩色下划线）。
  if (primaryColor) {
    return { primaryColor, hideLogo: true }
  }

  // Tier 5 — 无任何可解析视觉：省略前导列、无下划线。
  return { hideLogo: true }
}
